// Hyperliquid's candles for the market (papertrade prices at Hyperliquid's mid), with what
// matters drawn on them: each open position's entry and bust, and the bust the ticket would
// have if sent now. Candle values are for drawing only; no figure here is counted.

import { useEffect, useRef, useState } from "react";
import {
  CandlestickSeries,
  ColorType,
  CrosshairMode,
  LineStyle,
  createChart,
  type AutoscaleInfo,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
  type UTCTimestamp,
} from "lightweight-charts";
import type { Instrument } from "../../sdk";
import { candles as fetchCandles, INTERVALS, type Interval } from "../../hl/info";
import { onCandle, type Candle } from "../../hl/feed";
import { Segmented } from "../../components/ui";
import { useAccount } from "../../state/account";
import { usePref, setPref } from "../../state/prefs";
import { useStore } from "../../state/store";
import { fromRaw, scalePlaces } from "../../lib/num";
import { preview as previewStore, ticket as ticketStore } from "./ticket-state";

type Palette = { bg: string; text: string; grid: string; line: string; up: string; down: string; muted: string; warn: string; ink: string };

function palette(): Palette {
  const css = getComputedStyle(document.documentElement);
  const read = (name: string) => css.getPropertyValue(name).trim();
  return {
    bg: read("--c-surface"),
    text: read("--c-muted"),
    grid: read("--c-line"),
    line: read("--c-line-strong"),
    up: read("--c-up"),
    down: read("--c-down"),
    muted: read("--c-ink-2"),
    warn: read("--c-warn"),
    ink: read("--c-ink"),
  };
}

const bar = (candle: Candle) => ({
  time: Math.floor(candle.t / 1000) as UTCTimestamp,
  open: Number(candle.o),
  high: Number(candle.h),
  low: Number(candle.l),
  close: Number(candle.c),
});

/** A raw price at `scale` as the chart's number: for drawing only. */
const level = (raw: string, scale: string) => Number(fromRaw(raw, scalePlaces(scale)));

export function Chart({ instrument }: { instrument: Instrument }) {
  const host = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const linesRef = useRef<IPriceLine[]>([]);
  const previewLineRef = useRef<IPriceLine | null>(null);
  /** The levels drawn, as the autoscale reads them. */
  const levelsRef = useRef<number[]>([]);
  const positionLevels = useRef<number[]>([]);
  const previewLevel = useRef<number | null>(null);
  const [colors, setColors] = useState<Palette | null>(null);
  const [loading, setLoading] = useState(true);
  const interval = (usePref("interval") as Interval) ?? "5m";
  const theme = usePref("theme");
  const account = useAccount();
  const ticketPreview = useStore(previewStore);
  const side = useStore(ticketStore, (state) => state.side);
  const coin = instrument.symbol;
  const places = scalePlaces(instrument.priceScale);

  // the palette follows the theme, and the system's when it is the theme
  useEffect(() => {
    const read = () => requestAnimationFrame(() => setColors(palette()));
    read();
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    media.addEventListener("change", read);
    return () => media.removeEventListener("change", read);
  }, [theme]);

  // the chart itself, once
  useEffect(() => {
    if (!host.current) return;
    const chart = createChart(host.current, {
      autoSize: true,
      layout: { background: { type: ColorType.Solid, color: "transparent" }, fontFamily: "IBM Plex Mono, ui-monospace, monospace", fontSize: 11, attributionLogo: false },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { scaleMargins: { top: 0.12, bottom: 0.08 } },
      timeScale: { timeVisible: true, secondsVisible: false, rightOffset: 6, barSpacing: 8 },
      handleScale: { axisPressedMouseMove: true },
    });
    const series = chart.addSeries(CandlestickSeries, {
      borderVisible: false,
      // entries and busts near the price stay on screen; a 1× bust far below does not squash the candles
      autoscaleInfoProvider: (base: () => AutoscaleInfo | null) => {
        const info = base();
        if (!info?.priceRange) return info;
        let { minValue, maxValue } = info.priceRange;
        const middle = (minValue + maxValue) / 2;
        for (const value of levelsRef.current) {
          if (Math.abs(value - middle) / middle > 0.03) continue;
          minValue = Math.min(minValue, value);
          maxValue = Math.max(maxValue, value);
        }
        return { ...info, priceRange: { minValue, maxValue } };
      },
    });
    chartRef.current = chart;
    seriesRef.current = series;
    return () => {
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      linesRef.current = [];
      previewLineRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!colors || !chartRef.current || !seriesRef.current) return;
    chartRef.current.applyOptions({
      layout: { textColor: colors.text },
      grid: { vertLines: { color: colors.grid }, horzLines: { color: colors.grid } },
      rightPriceScale: { borderColor: colors.line },
      timeScale: { borderColor: colors.line },
      crosshair: { vertLine: { color: colors.muted, labelBackgroundColor: colors.ink }, horzLine: { color: colors.muted, labelBackgroundColor: colors.ink } },
    });
    seriesRef.current.applyOptions({
      upColor: colors.up,
      downColor: colors.down,
      wickUpColor: colors.up,
      wickDownColor: colors.down,
    });
  }, [colors]);

  useEffect(() => {
    seriesRef.current?.applyOptions({ priceFormat: { type: "price", precision: places, minMove: 1 / 10 ** places } });
  }, [places]);

  // history, then live
  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;
    const abort = new AbortController();
    let live: (() => void) | null = null;
    let last = 0;
    setLoading(true);
    fetchCandles(coin, interval, 600, abort.signal)
      .then((history) => {
        if (abort.signal.aborted) return;
        const bars = history.map(bar);
        series.setData(bars);
        last = bars.at(-1)?.time ?? 0;
        chartRef.current?.timeScale().scrollToRealTime();
        setLoading(false);
        live = onCandle(coin, interval, (candle) => {
          const next = bar(candle);
          if (next.time < last) return;
          last = next.time;
          series.update(next);
        });
      })
      .catch(() => {
        if (!abort.signal.aborted) setLoading(false);
      });
    return () => {
      abort.abort();
      live?.();
    };
  }, [coin, interval]);

  // each open position here: its entry and its bust
  useEffect(() => {
    const series = seriesRef.current;
    if (!series || !colors) return;
    linesRef.current.forEach((line) => series.removePriceLine(line));
    linesRef.current = [];
    const mine = (account?.positions ?? []).filter((position) => position.instrumentId === instrument.id).slice(0, 8);
    positionLevels.current = mine.flatMap((position) => [level(position.entryPrice, instrument.priceScale), level(position.bustPrice, instrument.priceScale)]);
    levelsRef.current = [...positionLevels.current, ...(previewLevel.current === null ? [] : [previewLevel.current])];
    for (const position of mine) {
      const tone = position.isLong ? colors.up : colors.down;
      const tag = `${position.isLong ? "L" : "S"} ${position.leverage}×`;
      linesRef.current.push(
        series.createPriceLine({ price: level(position.entryPrice, instrument.priceScale), color: tone, lineWidth: 1, lineStyle: LineStyle.Dotted, axisLabelVisible: true, title: tag }),
        series.createPriceLine({ price: level(position.bustPrice, instrument.priceScale), color: colors.down, lineWidth: 1, lineStyle: LineStyle.Solid, axisLabelVisible: true, title: `bust ${tag}` }),
      );
    }
  }, [account?.positions, colors, instrument.id, instrument.priceScale]);

  // the ticket's bust, if it were sent now
  const previewBust = ticketPreview?.instrumentId === instrument.id ? ticketPreview.preview.bust : null;
  useEffect(() => {
    const series = seriesRef.current;
    if (!series || !colors) return;
    if (previewLineRef.current) series.removePriceLine(previewLineRef.current);
    previewLevel.current = previewBust ? level(previewBust, instrument.priceScale) : null;
    levelsRef.current = [...positionLevels.current, ...(previewLevel.current === null ? [] : [previewLevel.current])];
    previewLineRef.current = previewBust
      ? series.createPriceLine({
          price: level(previewBust, instrument.priceScale),
          color: colors.warn,
          lineWidth: 1,
          lineStyle: LineStyle.Dashed,
          axisLabelVisible: true,
          title: `${side === "long" ? "long" : "short"} bust`,
        })
      : null;
  }, [previewBust, colors, instrument.priceScale, side]);

  return (
    <div className="ch">
      <div className="ch-tools">
        <Segmented<Interval>
          label="Interval"
          size="sm"
          value={interval}
          onChange={(next) => setPref("interval", next)}
          options={INTERVALS.map((value) => ({ value, label: value }))}
        />
        <span className="ch-legend">
          <span className="ch-key ch-key-bust" /> bust if sent now
        </span>
      </div>
      <div className="ch-host" ref={host}>
        {loading && <div className="ch-loading label">Loading {coin} candles</div>}
      </div>
    </div>
  );
}
